import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  ReplyClasses,
  formatDateTimeWithRelative,
  messages,
  type InboundMessageSummary,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { usePollingInterval, useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: 'open', label: messages.inbox.tabOpen, key: 'open' },
  { value: 'unlinked', label: messages.inbox.tabNeedsLink, key: 'unlinked' },
  { value: '', label: messages.inbox.tabAll, key: null },
];

/** S50 受信一覧 */
export const InboxPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { filters, setFilter } = useUrlFilters({
    handlingStatus: 'open',
    replyClass: '',
    assigneeId: '',
    unlinkedOnly: '',
  });

  const tab = filters.unlinkedOnly === 'true' ? 'unlinked' : (filters.handlingStatus as string);

  const query = {
    handlingStatus: filters.unlinkedOnly === 'true' ? undefined : filters.handlingStatus || undefined,
    replyClass: filters.replyClass || undefined,
    assigneeId: filters.assigneeId || undefined,
    unlinkedOnly: filters.unlinkedOnly || undefined,
    limit: 50,
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.inbox(query),
    queryFn: () => api.get<ListResponse<InboundMessageSummary>>('/inbox', query),
    refetchInterval: usePollingInterval(true, 30_000),
  });

  const sync = useMutation({
    mutationFn: () => api.post('/inbox/sync'),
    onSuccess: () => {
      toast.success('受信の取得を開始しました');
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['inbox'] }), 3000);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const counts = (data?.meta?.counts ?? {}) as Record<string, number>;

  const setTab = (value: string) => {
    if (value === 'unlinked') {
      setFilter('unlinkedOnly', 'true');
      setFilter('handlingStatus', '');
    } else {
      setFilter('unlinkedOnly', '');
      setFilter('handlingStatus', value);
    }
  };

  return (
    <>
      <PageHeader
        title={messages.inbox.title}
        actions={
          <Button variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw aria-hidden className={cn(sync.isPending && 'animate-spin')} />
            {messages.inbox.sync}
          </Button>
        }
      />

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="scroll-x mb-3 flex gap-1 border-b border-border" aria-label="受信の絞り込み">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.value || 'all'}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
              {item.key && counts[item.key] > 0 && (
                <span className="tabular ml-1.5 font-semibold">{counts[item.key]}</span>
              )}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <FilterChips className="mb-3">
          <Select
            value={filters.replyClass as string}
            onChange={(e) => setFilter('replyClass', e.target.value)}
            aria-label="分類で絞り込む"
            className="h-8 w-auto shrink-0 text-note lg:h-8"
          >
            <option value="">分類：すべて</option>
            {ReplyClasses.list.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Select>
        </FilterChips>

        <Tabs.Content value={tab}>
          {isLoading ? (
            <SkeletonRows rows={5} />
          ) : !data || data.data.length === 0 ? (
            <EmptyState
              title={messages.empty.inbox.title}
              description={messages.empty.inbox.description}
              action={{ label: messages.empty.inbox.action, onClick: () => sync.mutate() }}
              icon={<Inbox className="size-8" />}
            />
          ) : (
            <ul className="space-y-2">
              {data.data.map((item) => (
                <li key={item.id}>
                  <Card
                    className={cn(
                      'bg-background',
                      // 自動返信は淡色で折りたたむ
                      item.isAutoReply && 'opacity-60',
                      !item.companyId && 'border-2 border-foreground',
                    )}
                  >
                    <CardContent className="p-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/inbox/${item.id}`)}
                        className="w-full space-y-1.5 text-left"
                      >
                        {!item.companyId && (
                          <p className="flex items-center gap-1.5 text-body font-semibold">
                            <AlertTriangle aria-hidden className="size-4" />
                            {messages.inbox.unlinked}
                          </p>
                        )}
                        <p className="flex flex-wrap items-center gap-2">
                          <span className="min-w-0 truncate text-body font-semibold">
                            {item.companyName ?? item.fromEmail}
                          </span>
                          {item.replyClass && (
                            <StatusBadge kind="reply_class" value={item.replyClass} />
                          )}
                          <StatusBadge
                            kind="handling_status"
                            value={item.handlingStatus}
                            emphasis={item.handlingStatus === 'open' ? 'high' : 'default'}
                          />
                        </p>
                        <p className="truncate text-body text-foreground-muted">
                          {item.subject ?? '（件名なし）'}
                        </p>
                        {item.preview && (
                          <p className="line-clamp-2 text-note text-foreground-muted">{item.preview}</p>
                        )}
                        <p className="text-note text-foreground-subtle">
                          {formatDateTimeWithRelative(item.receivedAt)}
                          {item.matchCandidateCount > 0 &&
                            !item.companyId &&
                            ` ・ ${messages.inbox.matchCandidates(item.matchCandidateCount)}`}
                        </p>
                      </button>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
};
